// Bundle Upgrades tab — transforms API data, sorting, filtering, pagination, expand/collapse
import * as bootstrap from "bootstrap"

document.addEventListener("turbo:load", function () {
  var bundleTab = document.getElementById("bundle-upgrades")
  if (!bundleTab) return

  var tableContainer = bundleTab.querySelector(".bu-table-container")
  if (!tableContainer) return

  var ICON_TRASH = tableContainer.dataset.iconTrash || ""
  var ICON_PENCIL = tableContainer.dataset.iconPencil || ""
  var GUEST_ID = tableContainer.dataset.guestId || ""
  var RETURN_TO = tableContainer.dataset.returnTo || ""

  // --- Parse real data from ERB-serialized JSON ---
  var rawData = []
  try {
    rawData = JSON.parse(tableContainer.dataset.bundleUpgrades || "[]")
  } catch (e) {
    console.error("Failed to parse bundle upgrades data", e)
  }

  // --- Event type mappings ---
  // Bundle-upgrade event types emitted by the Go benefits service:
  // removed, redeemed, refunded, updated
  var EVENT_LABELS = {
    removed:  "Withdrawn",
    redeemed: "Applied to PNR",
    refunded: "Removed from PNR",
    updated:  "Modified"
  }

  function eventPnr(event, fallbackMeta) {
    if (event.metadata && event.metadata.pnr) return event.metadata.pnr
    if (fallbackMeta && fallbackMeta.pnr) return fallbackMeta.pnr
    return null
  }

  function eventTimelineTitle(event, fallbackMeta) {
    var pnr = eventPnr(event, fallbackMeta)
    switch (event.event_type) {
      case "removed":
        return "Bundle Upgrade withdrawn."
      case "redeemed":
        return pnr ? "Applied to PNR (" + pnr + ")." : "Applied to PNR."
      case "refunded":
        return pnr ? "Removed from PNR (" + pnr + ")." : "Removed from PNR."
      case "updated":
        return "Bundle Upgrade modified."
      default:
        return event.event_type + "."
    }
  }

  function eventLastChangeLabel(event) {
    return EVENT_LABELS[event.event_type] || event.event_type
  }

  // --- Status derivation (mirrors BundleUpgradeHelper) ---
  var STATUS_ORDER = { available: 0, redeemed: 1, expired: 2, withdrawn: 3 }

  function deriveStatus(item) {
    var events = item.events || []
    if (events.some(function (e) { return e.event_type === "removed" }) || item.metadata?.withdrawn_at) return "withdrawn"

    // Redeemed if the most recent redeemed/refunded event is `redeemed`.
    // A later `refunded` (e.g. booking cancellation) supersedes the redemption.
    var redemptionEvents = events.filter(function (e) {
      return e.event_type === "redeemed" || e.event_type === "refunded"
    })
    if (redemptionEvents.length > 0) {
      var latest = redemptionEvents.reduce(function (a, b) {
        return (a.occurred_at || "") > (b.occurred_at || "") ? a : b
      })
      if (latest.event_type === "redeemed") return "redeemed"
    }

    if (item.expires_at && new Date(item.expires_at) <= new Date()) return "expired"
    return "available"
  }

  // --- Timeline date formatting: MM/DD/YY . HH:MM AM/PM . Actor ---
  function formatTimelineDate(event) {
    if (!event.occurred_at) return "—"
    var d = new Date(event.occurred_at)
    var mm = String(d.getMonth() + 1).padStart(2, "0")
    var dd = String(d.getDate()).padStart(2, "0")
    var yy = String(d.getFullYear()).slice(-2)
    var hours = d.getHours()
    var ampm = hours >= 12 ? "PM" : "AM"
    hours = hours % 12 || 12
    var mins = String(d.getMinutes()).padStart(2, "0")
    var dateStr = mm + "/" + dd + "/" + yy + " \u2022 " + hours + ":" + mins + " " + ampm

    var actor = null
    if (event.actor_first_name || event.actor_last_name) {
      actor = [event.actor_first_name, event.actor_last_name].filter(Boolean).join(" ")
    }
    if (!actor && event.metadata) {
      actor = event.metadata.last_modified_by_admin_name
    }
    dateStr += " \u2022 " + (actor || "System")

    return dateStr
  }

  // --- Transform raw API data into table rows ---
  function transformData(data) {
    // Sort by guest_benefit_id ascending for numbering
    var sorted = data.slice().sort(function (a, b) {
      return (a.guest_benefit_id || 0) - (b.guest_benefit_id || 0)
    })

    return sorted.map(function (item, index) {
      var events = (item.events || []).slice()
      // Sort events newest-first
      events.sort(function (a, b) { return (b.occurred_at || "").localeCompare(a.occurred_at || "") })

      var latestEvent = events[0]
      var meta = item.metadata || {}
      var status = deriveStatus(item)

      // Build a unified list of timeline candidates with raw occurred_at so
      // synthesized entries (withdrawn, expired, created) sort naturally
      // alongside real events.
      // Prefer event.metadata.pnr (stamped on redeem/refund). Fall back to live
      // guest_benefit metadata for older redeemed events written before PNR
      // was stored on the event itself.
      var candidates = events.map(function (e) {
        var fallbackMeta = (e.event_type === "redeemed" && !(e.metadata && e.metadata.pnr)) ? meta : null
        return {
          occurred_at: e.occurred_at,
          title: eventTimelineTitle(e, fallbackMeta),
          reason: e.metadata && e.metadata.reason,
          actorMeta: e.metadata,
          actor_first_name: e.actor_first_name,
          actor_last_name: e.actor_last_name
        }
      })

      // The benefits backend does not emit an expiration event, so synthesize
      // one from expires_at whenever the row's current status is Expired.
      if (status === "expired" && item.expires_at) {
        candidates.push({
          occurred_at: item.expires_at,
          title: "Bundle Upgrade expired."
        })
      }

      // Synthesize a created event using created_at and metadata if present.
      candidates.push({
        occurred_at: item.created_at,
        title: meta.created_at ? "Bundle Upgrade manually created." : "Bundle Upgrade earned.",
        reason: meta.created_reason,
        actorMeta: meta.created_at ? { admin_name: meta.created_by_admin_name } : undefined
      })

      if (item.flown_at) {
        candidates.push({
          occurred_at: item.flown_at,
          title: "Flown on."
        })
      }

      candidates.sort(function (a, b) {
        return (b.occurred_at || "").localeCompare(a.occurred_at || "")
      })

      var timeline = candidates.map(function (c) {
        return {
          title: c.title,
          date: formatTimelineDate({ occurred_at: c.occurred_at, metadata: c.actorMeta, actor_first_name: c.actor_first_name, actor_last_name: c.actor_last_name }),
          reason: c.reason
        }
      })

      // No events → default "Earned" entry stamped with the guest benefit's
      // created_at, since the benefits service does not emit a `created` event.
      var effectiveLatest = latestEvent || { occurred_at: item.created_at }
      var effectiveLabel = latestEvent ? eventLastChangeLabel(latestEvent) : "Earned"
      if (meta.withdrawn_at) {
        effectiveLatest = { occurred_at: meta.withdrawn_at }
        effectiveLabel = "Withdrawn"
      }
      // When the row is Expired, Last Change reflects the expires_at date
      // since no real event marks the expiration.
      if (status === "expired") {
        effectiveLatest = { occurred_at: item.expires_at }
        effectiveLabel = "Expired"
      }
      // "Flown on" overrides the Redeemed last-change label once the redeemed
      // journey has departed. flown_at (set server-side) is the journey's
      // designator.departure (local station time), used purely for display.
      if (status === "redeemed" && item.flown_at) {
        effectiveLatest = { occurred_at: item.flown_at }
        effectiveLabel = "Flown on"
      }

      return {
        id: item.guest_benefit_id,
        number: index + 1,
        created: item.created_at,
        expires: item.expires_at,
        status: status,
        pnr: item.metadata?.pnr || null,
        lastChange: effectiveLatest ? effectiveLatest.occurred_at : null,
        lastChangeLabel: effectiveLabel,
        timeline: timeline
      }
    })
  }

  var UPGRADES = transformData(rawData)

  var PAGE_SIZE = 10
  var currentSort = { column: "lastChange", direction: "desc" }
  var currentPage = 1
  var expandedRows = new Set()

  // --- Filtering ---
  function getActiveFilters() {
    var checkboxes = bundleTab.querySelectorAll(".bu-filter-options .form-check-input")
    var checked = []
    checkboxes.forEach(function (cb) { if (cb.checked) checked.push(cb.value) })
    return checked
  }

  function getFilteredData() {
    var filters = getActiveFilters()
    if (filters.length === 0) return UPGRADES.slice()
    return UPGRADES.filter(function (u) { return filters.indexOf(u.status) !== -1 })
  }

  // --- Sorting ---
  function compareDates(a, b) {
    var da = a ? new Date(a).getTime() : 0
    var db = b ? new Date(b).getTime() : 0
    return da - db
  }

  function sortData(data) {
    var col = currentSort.column
    var asc = currentSort.direction === "asc"

    return data.sort(function (a, b) {
      var result = 0

      switch (col) {
        case "number":
          result = a.number - b.number
          if (!asc) result = -result
          break

        case "created":
          result = compareDates(a.created, b.created)
          if (!asc) result = -result
          if (result === 0) { result = compareDates(a.expires, b.expires); if (!asc) result = -result }
          if (result === 0) { result = compareDates(a.lastChange, b.lastChange); if (!asc) result = -result }
          if (result === 0) { result = a.number - b.number; if (!asc) result = -result }
          break

        case "expires":
          result = compareDates(a.expires, b.expires)
          if (!asc) result = -result
          if (result === 0) { result = compareDates(a.created, b.created); if (!asc) result = -result }
          if (result === 0) { result = compareDates(a.lastChange, b.lastChange); if (!asc) result = -result }
          if (result === 0) { result = a.number - b.number; if (!asc) result = -result }
          break

        case "status":
          result = (STATUS_ORDER[a.status] || 0) - (STATUS_ORDER[b.status] || 0)
          if (!asc) result = -result
          if (result === 0) { result = compareDates(b.lastChange, a.lastChange); if (!asc) result = -result }
          if (result === 0) { result = compareDates(a.expires, b.expires); if (!asc) result = -result }
          if (result === 0) { result = compareDates(b.created, a.created); if (!asc) result = -result }
          if (result === 0) { result = b.number - a.number; if (!asc) result = -result }
          break

        case "pnr":
          var hasPnrA = a.pnr ? 1 : 0
          var hasPnrB = b.pnr ? 1 : 0
          result = hasPnrB - hasPnrA
          if (!asc) result = -result
          if (result === 0 && a.pnr && b.pnr) { result = a.pnr.localeCompare(b.pnr); if (!asc) result = -result }
          if (result === 0 && !a.pnr && !b.pnr) { result = compareDates(b.lastChange, a.lastChange) }
          break

        case "lastChange":
          result = compareDates(b.lastChange, a.lastChange)
          if (asc) result = -result
          if (result === 0) { result = compareDates(b.created, a.created); if (asc) result = -result }
          if (result === 0) { result = compareDates(b.expires, a.expires); if (asc) result = -result }
          if (result === 0) { result = b.number - a.number; if (asc) result = -result }
          break
      }

      return result
    })
  }

  // --- Date formatting (MM/DD/YY) ---
  function formatDate(dateStr) {
    if (!dateStr) return "—"
    var d = new Date(dateStr)
    var mm = String(d.getMonth() + 1).padStart(2, "0")
    var dd = String(d.getDate()).padStart(2, "0")
    var yy = String(d.getFullYear()).slice(-2)
    return mm + "/" + dd + "/" + yy
  }

  function escapeHtml(str) {
    if (!str) return ""
    var div = document.createElement("div")
    div.appendChild(document.createTextNode(str))
    return div.innerHTML
  }

  function capitalize(str) {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  function buildUpdateUrl(guestBenefitId) {
    var url = "/guest_lookup/guest_details/" + encodeURIComponent(GUEST_ID) + "/bundle_upgrades/" + encodeURIComponent(guestBenefitId)
    if (RETURN_TO) url += "?return_to=" + encodeURIComponent(RETURN_TO)
    return url
  }

  // --- Rendering ---
  function renderTable() {
    var filtered = getFilteredData()
    var sorted = sortData(filtered)
    var totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
    if (currentPage > totalPages) currentPage = totalPages
    var start = (currentPage - 1) * PAGE_SIZE
    var pageData = sorted.slice(start, start + PAGE_SIZE)

    var tbody = document.getElementById("bu-table-body")
    tbody.innerHTML = ""

    pageData.forEach(function (upgrade, index) {
      var rowClass = index % 2 === 0 ? "bu-row-even" : "bu-row-odd"
      var isExpanded = expandedRows.has(upgrade.number)
      var updateUrl = buildUpdateUrl(upgrade.id)

      var tr = document.createElement("tr")
      tr.className = "bu-row " + rowClass
      tr.setAttribute("data-status", upgrade.status)
      tr.setAttribute("data-number", upgrade.number)
      tr.innerHTML =
        '<td data-th="Number">' + upgrade.number + '</td>' +
        '<td data-th="Created">' + formatDate(upgrade.created) + '</td>' +
        '<td data-th="Expires">' + formatDate(upgrade.expires) + '</td>' +
        '<td data-th="Status"><span class="badge-status badge-' + upgrade.status + '">' + capitalize(upgrade.status) + '</span></td>' +
        '<td data-th="PNR">' + (upgrade.pnr ? escapeHtml(upgrade.pnr) : '---') + '</td>' +
        '<td data-th="Last Change" class="bu-last-change-td">' +
          '<div class="bu-last-change-cell">' +
            '<span>' + escapeHtml(upgrade.lastChangeLabel) + (upgrade.lastChange ? ' &bull; ' + formatDate(upgrade.lastChange) : '') + '</span>' +
            '<div class="bu-actions-cell">' +
              '<button type="button" class="bu-action-btn bu-action-withdraw"' +
                ' data-bs-toggle="modal" data-bs-target="#withdrawBundleUpgradeModal"' +
                ' data-bundle-upgrade-number="' + upgrade.number + '"' +
                ' data-original-expiration-date="' + (upgrade.expires ? upgrade.expires.slice(0, 10) : '') + '"' +
                ' data-update-url="' + escapeHtml(updateUrl) + '"' +
              '><img src="' + ICON_TRASH + '" alt="" class="bu-action-icon"> Withdraw</button>' +
              '<button type="button" class="bu-action-btn bu-action-modify"' +
                ' data-bs-toggle="modal" data-bs-target="#modifyBundleUpgradeModal"' +
                ' data-bundle-upgrade-number="' + upgrade.number + '"' +
                ' data-original-expiration-date="' + (upgrade.expires ? upgrade.expires.slice(0, 10) : '') + '"' +
                ' data-update-url="' + escapeHtml(updateUrl) + '"' +
              '><img src="' + ICON_PENCIL + '" alt="" class="bu-action-icon"> Modify</button>' +
            '</div>' +
          '</div>' +
        '</td>'

      tr.addEventListener("click", function (e) {
        if (e.target.closest(".bu-action-btn")) return
        toggleExpand(upgrade.number)
      })

      tbody.appendChild(tr)

      // Detail/timeline row
      var detailTr = document.createElement("tr")
      detailTr.className = "bu-detail-row " + rowClass
      var timelineHtml = upgrade.timeline.length > 0
        ? renderTimeline(upgrade.timeline)
        : '<div class="bu-timeline"><div class="bu-timeline-event"><div class="bu-timeline-dot-col"><div class="bu-timeline-dot"></div><div class="bu-timeline-line"></div></div><div class="bu-timeline-content"><div class="bu-timeline-title">No event history available.</div></div></div></div>'

      detailTr.innerHTML =
        '<td class="bu-detail-spacer"></td>' +
        '<td colspan="5">' +
          '<div class="collapse' + (isExpanded ? ' show' : '') + '" id="bu-detail-' + upgrade.number + '">' +
            '<div class="bu-detail-cell">' +
              timelineHtml +
            '</div>' +
          '</div>' +
        '</td>'
      tbody.appendChild(detailTr)
    })

    renderPagination(totalPages, sorted.length)
    updateSortHeaders()
  }

  function renderTimeline(events) {
    if (!events || events.length === 0) return ""

    var html = '<div class="bu-timeline">'
    events.forEach(function (event) {
      var hasReason = !!event.reason
      var contentClass = "bu-timeline-content" + (hasReason ? " has-reason" : "")

      var reasonHtml = ""
      if (hasReason) {
        reasonHtml =
          '<div class="bu-timeline-message-card">' +
            '<span class="bu-timeline-reason-label">Reason</span>' +
            '<p class="bu-timeline-reason-text">' + escapeHtml(event.reason) + '</p>' +
          '</div>'
      }

      html +=
        '<div class="bu-timeline-event">' +
          '<div class="bu-timeline-dot-col">' +
            '<div class="bu-timeline-dot"></div>' +
            '<div class="bu-timeline-line"></div>' +
          '</div>' +
          '<div class="' + contentClass + '">' +
            '<div class="bu-timeline-title">' + escapeHtml(event.title) + '</div>' +
            reasonHtml +
            '<div class="bu-timeline-date">' + escapeHtml(event.date) + '</div>' +
          '</div>' +
        '</div>'
    })
    html += '</div>'
    return html
  }

  // --- Expand/Collapse ---
  function toggleExpand(number) {
    var detailEl = document.getElementById("bu-detail-" + number)
    if (!detailEl) return

    if (expandedRows.has(number)) {
      expandedRows.delete(number)
      bootstrap.Collapse.getOrCreateInstance(detailEl).hide()
    } else {
      expandedRows.add(number)
      bootstrap.Collapse.getOrCreateInstance(detailEl).show()
    }
  }

  // --- Pagination ---
  function renderPagination(totalPages, totalItems) {
    var container = document.getElementById("bu-pagination")
    if (totalPages <= 1) { container.innerHTML = ""; return }

    var start = (currentPage - 1) * PAGE_SIZE + 1
    var end = Math.min(currentPage * PAGE_SIZE, totalItems)

    var html = ''
    html += '<div class="bu-pagination__results">Showing ' + start + ' to ' + end + ' of ' + totalItems + ' results</div>'
    html += '<nav class="bu-pagination__nav" aria-label="Bundle Upgrades pagination">'
    html += '<button class="bu-pagination__btn bu-pagination__btn--prev" data-page="' + (currentPage - 1) + '"' + (currentPage === 1 ? ' disabled' : '') + '>'
    html += '<svg class="bu-pagination__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.77983 4.22001C9.92028 4.36064 9.99917 4.55126 9.99917 4.75001C9.99917 4.94876 9.92028 5.13939 9.77983 5.28001L7.05983 8.00001L9.77983 10.72C9.85351 10.7887 9.91262 10.8715 9.95361 10.9635C9.9946 11.0555 10.0166 11.1548 10.0184 11.2555C10.0202 11.3562 10.0017 11.4562 9.96395 11.5496C9.92623 11.643 9.87009 11.7278 9.79887 11.799C9.72765 11.8703 9.64281 11.9264 9.54943 11.9641C9.45604 12.0019 9.35601 12.0204 9.25531 12.0186C9.1546 12.0168 9.05529 11.9948 8.96329 11.9538C8.87129 11.9128 8.78849 11.8537 8.71983 11.78L5.46983 8.53001C5.32938 8.38939 5.25049 8.19876 5.25049 8.00001C5.25049 7.80126 5.32938 7.61064 5.46983 7.47001L8.71983 4.22001C8.86045 4.07956 9.05108 4.00067 9.24983 4.00067C9.44858 4.00067 9.6392 4.07956 9.77983 4.22001Z" fill="currentColor"/></svg><span>Previous</span>'
    html += '</button>'
    html += '<div class="bu-pagination__pages">'
    for (var p = 1; p <= totalPages; p++) {
      html += '<button class="bu-pagination__page' + (p === currentPage ? ' active' : '') + '" data-page="' + p + '">' + p + '</button>'
    }
    html += '</div>'
    html += '<button class="bu-pagination__btn bu-pagination__btn--next" data-page="' + (currentPage + 1) + '"' + (currentPage === totalPages ? ' disabled' : '') + '>'
    html += '<span>Next</span><svg class="bu-pagination__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.77983 4.22001C9.92028 4.36064 9.99917 4.55126 9.99917 4.75001C9.99917 4.94876 9.92028 5.13939 9.77983 5.28001L7.05983 8.00001L9.77983 10.72C9.85351 10.7887 9.91262 10.8715 9.95361 10.9635C9.9946 11.0555 10.0166 11.1548 10.0184 11.2555C10.0202 11.3562 10.0017 11.4562 9.96395 11.5496C9.92623 11.643 9.87009 11.7278 9.79887 11.799C9.72765 11.8703 9.64281 11.9264 9.54943 11.9641C9.45604 12.0019 9.35601 12.0204 9.25531 12.0186C9.1546 12.0168 9.05529 11.9948 8.96329 11.9538C8.87129 11.9128 8.78849 11.8537 8.71983 11.78L5.46983 8.53001C5.32938 8.38939 5.25049 8.19876 5.25049 8.00001C5.25049 7.80126 5.32938 7.61064 5.46983 7.47001L8.71983 4.22001C8.86045 4.07956 9.05108 4.00067 9.24983 4.00067C9.44858 4.00067 9.6392 4.07956 9.77983 4.22001Z" transform="translate(16,0) scale(-1,1)" fill="currentColor"/></svg>'
    html += '</button>'
    html += '</nav>'
    container.innerHTML = html

    container.querySelectorAll("[data-page]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault()
        var page = parseInt(btn.dataset.page)
        if (page >= 1 && page <= totalPages && page !== currentPage) {
          currentPage = page
          expandedRows.clear()
          renderTable()
        }
      })
    })
  }

  // --- Sort header active state ---
  function updateSortHeaders() {
    bundleTab.querySelectorAll(".bu-table thead th[data-sort]").forEach(function (th) {
      if (th.dataset.sort === currentSort.column) {
        th.classList.add("bu-sort-active")
      } else {
        th.classList.remove("bu-sort-active")
      }
    })
  }

  // --- Event bindings ---
  bundleTab.querySelectorAll(".bu-table thead th[data-sort]").forEach(function (th) {
    th.addEventListener("click", function () {
      var col = th.dataset.sort
      if (currentSort.column === col) {
        currentSort.direction = currentSort.direction === "asc" ? "desc" : "asc"
      } else {
        currentSort.column = col
        currentSort.direction = col === "lastChange" ? "desc" : "asc"
      }
      currentPage = 1
      expandedRows.clear()
      renderTable()
    })
  })

  bundleTab.querySelectorAll(".bu-filter-options .form-check-input").forEach(function (cb) {
    cb.addEventListener("change", function () {
      currentPage = 1
      expandedRows.clear()
      renderTable()
    })
  })

  // --- Initial render ---
  renderTable()
})
